import { zDecimal, zDecimalRequired } from '@harvestiq/zod';
import Decimal from 'decimal.js';
import { z } from '@harvestiq/zod';
import { CommodityId } from '../CommodityId';
import { RmaClassCodes } from '../rma/ClassCodes';
import {
  RmaCommodityTypeCodes,
  RmaCommodityTypeNames,
} from '../rma/CommodityTypeCodes';
import {
  RmaLrpCommodityCodes,
  RmaLrpCommodityNames,
  RmaLrpCommodityCode,
} from './LrpCommodities';
import {
  RmaLrpTypeCodes,
  RmaLrpTypeCodeNames,
  HiqLrpTypeCodeNames,
  RmaLrpTypeCode,
} from './LrpTypeCodes';

const feederCattleWeightClass1WeightRange = {
  min: 1,
  max: 5.99,
};
const feederCattleWeightClass2WeightRange = {
  min: 6,
  max: 10,
};
const feederCattleUnbornCalvesWeightRange = {
  min: 0.6,
  max: 0.99,
};
const fedCattleSteersAndHeifersWeightRange = {
  min: 10,
  max: 16,
};
const fedCattleCullCowsWeightRange = {
  min: 8,
  max: 15,
};
const swineWeightRange = {
  min: 1.4,
  max: 2.6,
};
const LrpCommodityInfoEntrySchema = z.object({
  rmaTypeCode: z.nativeEnum(RmaLrpTypeCodes),
  rmaTypeName: z.nativeEnum(RmaLrpTypeCodeNames),
  hiqTypeCodeName: z.nativeEnum(HiqLrpTypeCodeNames),
  rmaCommodityCode: z.nativeEnum(RmaLrpCommodityCodes),
  rmaCommodityName: z.nativeEnum(RmaLrpCommodityNames),
  rmaCommodityTypeCode: z.nativeEnum(RmaCommodityTypeCodes),
  rmaCommodityTypeName: z.nativeEnum(RmaCommodityTypeNames),
  rmaClassCode: z.nativeEnum(RmaClassCodes),
  commodityId: z.nativeEnum(CommodityId),
  commodityTargetWeightRange: z.object({
    min: z.number(),
    max: z.number(),
  }),
  preRY2026PriceAdjustmentFactor: zDecimal().optional(),
  cmeOptionsName: z.string(),
});
type LrpCommodityInfoEntry = z.infer<typeof LrpCommodityInfoEntrySchema>;
/**
 * NOTE: Avoid accessing this object directly in external code. Use the `getLrpCommodityInfo` function instead.
 * This allows for a more controlled access pattern and easier future modifications.
 */
const LrpCommodityInfoByCommodityAndTypeCode = {
  [RmaLrpCommodityCodes.FEEDER_CATTLE]: {
    [RmaLrpTypeCodes.STEERS_WEIGHT_1]: {
      rmaTypeCode: RmaLrpTypeCodes.STEERS_WEIGHT_1,
      rmaTypeName: RmaLrpTypeCodeNames.STEERS_WEIGHT_1,
      rmaCommodityCode: RmaLrpCommodityCodes.FEEDER_CATTLE,
      hiqTypeCodeName: HiqLrpTypeCodeNames.STEERS_LIGHT,
      rmaCommodityName: RmaLrpCommodityNames.FEEDER_CATTLE,
      rmaCommodityTypeCode: RmaCommodityTypeCodes.Steers,
      rmaCommodityTypeName: RmaCommodityTypeNames.Steers,
      rmaClassCode: RmaClassCodes.Weight1,
      commodityId: CommodityId.FEEDER_CATTLE,
      commodityTargetWeightRange: feederCattleWeightClass1WeightRange,
      preRY2026PriceAdjustmentFactor: new Decimal(1.1),
      cmeOptionsName: 'CME Feeder Cattle Options',
    },
    [RmaLrpTypeCodes.STEERS_WEIGHT_2]: {
      rmaTypeCode: RmaLrpTypeCodes.STEERS_WEIGHT_2,
      rmaTypeName: RmaLrpTypeCodeNames.STEERS_WEIGHT_2,
      rmaCommodityCode: RmaLrpCommodityCodes.FEEDER_CATTLE,
      hiqTypeCodeName: HiqLrpTypeCodeNames.STEERS,
      rmaCommodityName: RmaLrpCommodityNames.FEEDER_CATTLE,
      rmaCommodityTypeCode: RmaCommodityTypeCodes.Steers,
      rmaCommodityTypeName: RmaCommodityTypeNames.Steers,
      rmaClassCode: RmaClassCodes.Weight2,
      commodityId: CommodityId.FEEDER_CATTLE,
      commodityTargetWeightRange: feederCattleWeightClass2WeightRange,
      preRY2026PriceAdjustmentFactor: new Decimal(1),
      cmeOptionsName: 'CME Feeder Cattle Options',
    },
    [RmaLrpTypeCodes.HEIFERS_WEIGHT_1]: {
      rmaTypeCode: RmaLrpTypeCodes.HEIFERS_WEIGHT_1,
      rmaTypeName: RmaLrpTypeCodeNames.HEIFERS_WEIGHT_1,
      rmaCommodityCode: RmaLrpCommodityCodes.FEEDER_CATTLE,
      hiqTypeCodeName: HiqLrpTypeCodeNames.HEIFERS_LIGHT,
      rmaCommodityName: RmaLrpCommodityNames.FEEDER_CATTLE,
      rmaCommodityTypeCode: RmaCommodityTypeCodes.Heifers,
      rmaCommodityTypeName: RmaCommodityTypeNames.Heifers,
      rmaClassCode: RmaClassCodes.Weight1,
      commodityId: CommodityId.FEEDER_CATTLE,
      commodityTargetWeightRange: feederCattleWeightClass1WeightRange,
      preRY2026PriceAdjustmentFactor: new Decimal(1),
      cmeOptionsName: 'CME Feeder Cattle Options',
    },
    [RmaLrpTypeCodes.HEIFERS_WEIGHT_2]: {
      rmaTypeCode: RmaLrpTypeCodes.HEIFERS_WEIGHT_2,
      rmaTypeName: RmaLrpTypeCodeNames.HEIFERS_WEIGHT_2,
      rmaCommodityCode: RmaLrpCommodityCodes.FEEDER_CATTLE,
      hiqTypeCodeName: HiqLrpTypeCodeNames.HEIFERS,
      rmaCommodityName: RmaLrpCommodityNames.FEEDER_CATTLE,
      rmaCommodityTypeCode: RmaCommodityTypeCodes.Heifers,
      rmaCommodityTypeName: RmaCommodityTypeNames.Heifers,
      rmaClassCode: RmaClassCodes.Weight2,
      commodityId: CommodityId.FEEDER_CATTLE,
      commodityTargetWeightRange: feederCattleWeightClass2WeightRange,
      preRY2026PriceAdjustmentFactor: new Decimal(0.9),
      cmeOptionsName: 'CME Feeder Cattle Options',
    },
    [RmaLrpTypeCodes.BRAHMAN_WEIGHT_1]: {
      rmaTypeCode: RmaLrpTypeCodes.BRAHMAN_WEIGHT_1,
      rmaTypeName: RmaLrpTypeCodeNames.BRAHMAN_WEIGHT_1,
      rmaCommodityCode: RmaLrpCommodityCodes.FEEDER_CATTLE,
      hiqTypeCodeName: HiqLrpTypeCodeNames.BRAHMAN_LIGHT,
      rmaCommodityName: RmaLrpCommodityNames.FEEDER_CATTLE,
      rmaCommodityTypeCode: RmaCommodityTypeCodes.Brahman,
      rmaCommodityTypeName: RmaCommodityTypeNames.Brahman,
      rmaClassCode: RmaClassCodes.Weight1,
      commodityId: CommodityId.FEEDER_CATTLE,
      commodityTargetWeightRange: feederCattleWeightClass1WeightRange,
      preRY2026PriceAdjustmentFactor: new Decimal(1),
      cmeOptionsName: 'CME Feeder Cattle Options',
    },
    [RmaLrpTypeCodes.BRAHMAN_WEIGHT_2]: {
      rmaTypeCode: RmaLrpTypeCodes.BRAHMAN_WEIGHT_2,
      rmaTypeName: RmaLrpTypeCodeNames.BRAHMAN_WEIGHT_2,
      rmaCommodityCode: RmaLrpCommodityCodes.FEEDER_CATTLE,
      hiqTypeCodeName: HiqLrpTypeCodeNames.BRAHMAN,
      rmaCommodityName: RmaLrpCommodityNames.FEEDER_CATTLE,
      rmaCommodityTypeCode: RmaCommodityTypeCodes.Brahman,
      rmaCommodityTypeName: RmaCommodityTypeNames.Brahman,
      rmaClassCode: RmaClassCodes.Weight2,
      commodityId: CommodityId.FEEDER_CATTLE,
      commodityTargetWeightRange: feederCattleWeightClass2WeightRange,
      preRY2026PriceAdjustmentFactor: new Decimal(1),
      cmeOptionsName: 'CME Feeder Cattle Options',
    },
    [RmaLrpTypeCodes.DAIRY_WEIGHT_1]: {
      rmaTypeCode: RmaLrpTypeCodes.DAIRY_WEIGHT_1,
      rmaTypeName: RmaLrpTypeCodeNames.DAIRY_WEIGHT_1,
      rmaCommodityCode: RmaLrpCommodityCodes.FEEDER_CATTLE,
      hiqTypeCodeName: HiqLrpTypeCodeNames.DAIRY_LIGHT,
      rmaCommodityName: RmaLrpCommodityNames.FEEDER_CATTLE,
      rmaCommodityTypeCode: RmaCommodityTypeCodes.Dairy,
      rmaCommodityTypeName: RmaCommodityTypeNames.Dairy,
      rmaClassCode: RmaClassCodes.Weight1,
      commodityId: CommodityId.FEEDER_CATTLE,
      commodityTargetWeightRange: feederCattleWeightClass1WeightRange,
      preRY2026PriceAdjustmentFactor: new Decimal(0.5),
      cmeOptionsName: 'CME Feeder Cattle Options',
    },
    [RmaLrpTypeCodes.DAIRY_WEIGHT_2]: {
      rmaTypeCode: RmaLrpTypeCodes.DAIRY_WEIGHT_2,
      rmaTypeName: RmaLrpTypeCodeNames.DAIRY_WEIGHT_2,
      rmaCommodityCode: RmaLrpCommodityCodes.FEEDER_CATTLE,
      hiqTypeCodeName: HiqLrpTypeCodeNames.DAIRY,
      rmaCommodityName: RmaLrpCommodityNames.FEEDER_CATTLE,
      rmaCommodityTypeCode: RmaCommodityTypeCodes.Dairy,
      rmaCommodityTypeName: RmaCommodityTypeNames.Dairy,
      rmaClassCode: RmaClassCodes.Weight2,
      commodityId: CommodityId.FEEDER_CATTLE,
      commodityTargetWeightRange: feederCattleWeightClass2WeightRange,
      preRY2026PriceAdjustmentFactor: new Decimal(0.5),
      cmeOptionsName: 'CME Feeder Cattle Options',
    },
    [RmaLrpTypeCodes.UNBORN_BULLS_AND_HEIFERS]: {
      rmaTypeCode: RmaLrpTypeCodes.UNBORN_BULLS_AND_HEIFERS,
      rmaTypeName: RmaLrpTypeCodeNames.UNBORN_BULLS_AND_HEIFERS,
      rmaCommodityCode: RmaLrpCommodityCodes.FEEDER_CATTLE,
      hiqTypeCodeName: HiqLrpTypeCodeNames.UNBORN_BULLS_AND_HEIFERS,
      rmaCommodityName: RmaLrpCommodityNames.FEEDER_CATTLE,
      rmaCommodityTypeCode: RmaCommodityTypeCodes.SteersAndHeifers,
      rmaCommodityTypeName: RmaCommodityTypeNames.SteersAndHeifers,
      rmaClassCode: RmaClassCodes.Unborn,
      commodityId: CommodityId.FEEDER_CATTLE,
      commodityTargetWeightRange: feederCattleWeightClass1WeightRange,
      preRY2026PriceAdjustmentFactor: new Decimal(1.05),
      cmeOptionsName: 'CME Feeder Cattle Options',
    },
    [RmaLrpTypeCodes.UNBORN_BRAHMAN]: {
      rmaTypeCode: RmaLrpTypeCodes.UNBORN_BRAHMAN,
      rmaTypeName: RmaLrpTypeCodeNames.UNBORN_BRAHMAN,
      rmaCommodityCode: RmaLrpCommodityCodes.FEEDER_CATTLE,
      hiqTypeCodeName: HiqLrpTypeCodeNames.UNBORN_BRAHMAN,
      rmaCommodityName: RmaLrpCommodityNames.FEEDER_CATTLE,
      rmaCommodityTypeCode: RmaCommodityTypeCodes.Brahman,
      rmaCommodityTypeName: RmaCommodityTypeNames.Brahman,
      rmaClassCode: RmaClassCodes.Unborn,
      commodityId: CommodityId.FEEDER_CATTLE,
      commodityTargetWeightRange: feederCattleWeightClass1WeightRange,
      preRY2026PriceAdjustmentFactor: new Decimal(1),
      cmeOptionsName: 'CME Feeder Cattle Options',
    },
    [RmaLrpTypeCodes.UNBORN_DAIRY]: {
      rmaTypeCode: RmaLrpTypeCodes.UNBORN_DAIRY,
      rmaTypeName: RmaLrpTypeCodeNames.UNBORN_DAIRY,
      rmaCommodityCode: RmaLrpCommodityCodes.FEEDER_CATTLE,
      hiqTypeCodeName: HiqLrpTypeCodeNames.UNBORN_DAIRY,
      rmaCommodityName: RmaLrpCommodityNames.FEEDER_CATTLE,
      rmaCommodityTypeCode: RmaCommodityTypeCodes.Dairy,
      rmaCommodityTypeName: RmaCommodityTypeNames.Dairy,
      rmaClassCode: RmaClassCodes.Unborn,
      commodityId: CommodityId.FEEDER_CATTLE,
      commodityTargetWeightRange: feederCattleWeightClass1WeightRange,
      preRY2026PriceAdjustmentFactor: new Decimal(0.5),
      cmeOptionsName: 'CME Feeder Cattle Options',
    },
    [RmaLrpTypeCodes.UNBORN_CALVES]: {
      rmaTypeCode: RmaLrpTypeCodes.UNBORN_CALVES,
      rmaTypeName: RmaLrpTypeCodeNames.UNBORN_CALVES,
      rmaCommodityCode: RmaLrpCommodityCodes.FEEDER_CATTLE,
      hiqTypeCodeName: HiqLrpTypeCodeNames.UNBORN_CALVES,
      rmaCommodityName: RmaLrpCommodityNames.FEEDER_CATTLE,
      rmaCommodityTypeCode: RmaCommodityTypeCodes.Calves,
      rmaCommodityTypeName: RmaCommodityTypeNames.Calves,
      rmaClassCode: RmaClassCodes.Unborn,
      commodityId: CommodityId.FEEDER_CATTLE,
      commodityTargetWeightRange: feederCattleUnbornCalvesWeightRange,
      // Unborn calves didn't exist prior to RY2026, so there is no price adjustment factor.
      cmeOptionsName: 'CME Feeder Cattle Options',
    },
  },
  [RmaLrpCommodityCodes.FED_CATTLE]: {
    [RmaLrpTypeCodes.STEERS_AND_HEIFERS]: {
      rmaTypeCode: RmaLrpTypeCodes.STEERS_AND_HEIFERS,
      rmaTypeName: RmaLrpTypeCodeNames.STEERS_AND_HEIFERS,
      rmaCommodityCode: RmaLrpCommodityCodes.FED_CATTLE,
      hiqTypeCodeName: HiqLrpTypeCodeNames.STEERS_HEIFERS,
      rmaCommodityName: RmaLrpCommodityNames.FED_CATTLE,
      rmaCommodityTypeCode: RmaCommodityTypeCodes.SteersAndHeifers,
      rmaCommodityTypeName: RmaCommodityTypeNames.SteersAndHeifers,
      rmaClassCode: RmaClassCodes.NoClassSpecified,
      commodityId: CommodityId.LIVE_CATTLE,
      commodityTargetWeightRange: fedCattleSteersAndHeifersWeightRange,
      cmeOptionsName: 'CME Live Cattle Options',
    },
    [RmaLrpTypeCodes.CULL_COWS]: {
      rmaTypeCode: RmaLrpTypeCodes.CULL_COWS,
      rmaTypeName: RmaLrpTypeCodeNames.CULL_COWS,
      rmaCommodityCode: RmaLrpCommodityCodes.FED_CATTLE,
      hiqTypeCodeName: HiqLrpTypeCodeNames.CULL_COWS,
      rmaCommodityName: RmaLrpCommodityNames.FED_CATTLE,
      rmaCommodityTypeCode: RmaCommodityTypeCodes.CullCows,
      rmaCommodityTypeName: RmaCommodityTypeNames.CullCows,
      rmaClassCode: RmaClassCodes.NoClassSpecified,
      commodityId: CommodityId.LIVE_CATTLE,
      commodityTargetWeightRange: fedCattleCullCowsWeightRange,
      cmeOptionsName: 'CME Live Cattle Options',
    },
  },
  [RmaLrpCommodityCodes.SWINE]: {
    [RmaLrpTypeCodes.UNBORN_SWINE]: {
      rmaTypeCode: RmaLrpTypeCodes.UNBORN_SWINE,
      rmaTypeName: RmaLrpTypeCodeNames.UNBORN_SWINE,
      rmaCommodityCode: RmaLrpCommodityCodes.SWINE,
      hiqTypeCodeName: HiqLrpTypeCodeNames.UNBORN_SWINE,
      rmaCommodityName: RmaLrpCommodityNames.SWINE,
      rmaCommodityTypeCode: RmaCommodityTypeCodes.NoCommodityTypeSpecified,
      rmaCommodityTypeName: RmaCommodityTypeNames.NoCommodityTypeSpecified,
      rmaClassCode: RmaClassCodes.Unborn,
      commodityId: CommodityId.LEAN_HOGS,
      commodityTargetWeightRange: swineWeightRange,
      cmeOptionsName: 'CME Lean Hog Options',
    },
    [RmaLrpTypeCodes.NO_TYPE_SPECIFIED]: {
      rmaTypeCode: RmaLrpTypeCodes.NO_TYPE_SPECIFIED,
      rmaTypeName: RmaLrpTypeCodeNames.NO_TYPE_SPECIFIED,
      rmaCommodityCode: RmaLrpCommodityCodes.SWINE,
      hiqTypeCodeName: HiqLrpTypeCodeNames.UNKNOWN,
      rmaCommodityName: RmaLrpCommodityNames.SWINE,
      rmaCommodityTypeCode: RmaCommodityTypeCodes.NoCommodityTypeSpecified,
      rmaCommodityTypeName: RmaCommodityTypeNames.NoCommodityTypeSpecified,
      rmaClassCode: RmaClassCodes.NoClassSpecified,
      commodityId: CommodityId.LEAN_HOGS,
      commodityTargetWeightRange: swineWeightRange,
      cmeOptionsName: 'CME Lean Hog Options',
    },
  },
};

export function getLrpCommodityInfo({
  commodityCode,
  typeCode,
}: {
  commodityCode: RmaLrpCommodityCode;
  typeCode: RmaLrpTypeCode;
}): LrpCommodityInfoEntry {
  // First check if the commodity code exists
  const commodityData = LrpCommodityInfoByCommodityAndTypeCode[commodityCode];
  if (!commodityData) {
    throw new Error(`Invalid commodity code: ${commodityCode}`);
  }

  // Then check if the type code exists for that commodity
  const lrpCommodityInfo =
    commodityData[typeCode as keyof typeof commodityData];
  if (!lrpCommodityInfo) {
    throw new Error(
      `Invalid type code ${typeCode} for commodity ${commodityCode}`
    );
  }

  return lrpCommodityInfo;
}
/**
 * Flattens the nested LrpCommodityInfo object into a single array of LrpCommodityInfoEntry objects
 */

export function getAllLrpCommodityInfo(): LrpCommodityInfoEntry[] {
  const allLrpCommodityInfo: LrpCommodityInfoEntry[] = [];

  Object.values(LrpCommodityInfoByCommodityAndTypeCode).forEach(
    (commodityData) => {
      Object.values(commodityData).forEach((lrpCommodityInfoEntry) => {
        allLrpCommodityInfo.push(lrpCommodityInfoEntry);
      });
    }
  );

  return allLrpCommodityInfo;
}

export function getRmaLrpTypeCodeOptions() {
  // Get all LRP commodity info entries
  return getAllLrpCommodityInfo().map((info) => ({
    label: info.hiqTypeCodeName,
    value: info.rmaTypeCode,
    commodityId: info.commodityId,
  }));
}

export function getRmaLrpTypeCodeOptionsForCommodity(
  commodityCode: RmaLrpCommodityCode
): LrpCommodityInfoEntry[] {
  const commodityData = LrpCommodityInfoByCommodityAndTypeCode[commodityCode];

  return Object.values(commodityData);
}

// check if type code belongs to commodity code
export function isLrpTypeCodeValidForLrpCommodity({
  commodityCode,
  typeCode,
}: {
  commodityCode: RmaLrpCommodityCode;
  typeCode: RmaLrpTypeCode;
}): boolean {
  const commodityData = LrpCommodityInfoByCommodityAndTypeCode[commodityCode];
  if (!commodityData) {
    return false;
  }
  return typeCode in commodityData;
}
